import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultEnum } from "../../types.client";
import { RfRecognitionDistanceMeasure } from "../../../models/schemas/measures/rfCard.schema";
import { RF_RECOGNITION_DISTANCE_THRESHOLDS } from "../../constants/frequencyThresholds";

/**
 * RF카드 인식거리 컨트롤러
 * 주파수대역별 상한:
 * - '125-134kHz': 0.1m (10cm)
 * - '13.56MHz-근접': 0.1m (10cm)
 * - '13.56MHz-근거리': 1.5m
 * - '860-960MHz': 12m
 * config.extra.frequencyBand 필수
 */
export class RfRecognitionDistanceController extends MeasureController<RfRecognitionDistanceMeasure> {
  protected getDefaultData(): RfRecognitionDistanceMeasure {
    return {
      label: "",
      recognitionDistance: 0,
      unit: "m",
      photo: { checkUrl: "", resultUrl: "" },
      result: null,
      discussion: "",
      remark: "",
      createdAt: null,
    };
  }

  protected judge(): JudgmentResult {
    const distance = this.data.recognitionDistance ?? 0;
    const frequencyBand = this.config.extra
      ?.frequencyBand as keyof typeof RF_RECOGNITION_DISTANCE_THRESHOLDS;

    // frequencyBand가 없으면 NA
    if (!frequencyBand) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: "주파수 대역 정보가 설정되지 않았습니다.",
      };
    }

    // 값이 0이면 NA
    if (distance === 0) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    // 주파수대역별 상한값 가져오기
    const threshold = RF_RECOGNITION_DISTANCE_THRESHOLDS[frequencyBand];

    if (threshold === undefined) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: `알 수 없는 주파수 대역: ${frequencyBand}`,
      };
    }

    // distance <= threshold이면 GOOD
    if (distance <= threshold) {
      return {
        result: JudgmentResultEnum.GOOD,
        discussion: this.generateDiscussion(JudgmentResultEnum.GOOD),
      };
    }

    // 상한 초과면 BAD
    return {
      result: JudgmentResultEnum.BAD,
      discussion: this.generateDiscussion(JudgmentResultEnum.BAD),
    };
  }

  protected generateDiscussion(result: "GOOD" | "CAUTION" | "BAD" | "NA"): string {
    const distance = this.data.recognitionDistance ?? 0;
    const frequencyBand = this.config.extra?.frequencyBand;

    if (result === JudgmentResultEnum.NA) {
      return "측정값 또는 주파수 대역이 입력되지 않았습니다.";
    }

    const threshold =
      RF_RECOGNITION_DISTANCE_THRESHOLDS[
        frequencyBand as keyof typeof RF_RECOGNITION_DISTANCE_THRESHOLDS
      ];

    if (result === JudgmentResultEnum.GOOD) {
      return `인식거리 ${distance}m는 ${frequencyBand} 기준(${threshold}m 이하) 범위 내입니다.`;
    }

    return `인식거리 ${distance}m는 ${frequencyBand} 기준(${threshold}m 이하)을 초과했습니다.`;
  }
}
